import { safeGetItem, safeKeys, safeRemoveItem, safeSetItem } from "utils/browser/localStorage";
import { logToSentry } from "utils/logger";
import { v4 as uuid } from "uuid";

/**
 * Persisted at-most-once registry for order-scoped analytics events (JID-2347).
 *
 * Order events are re-fired at the app level — component remounts, page reloads, and
 * concurrent ghost webview instances re-processing the same subscription frames. Their
 * deterministic `$insert_id` makes every re-fire collide with the original, but Mixpanel's
 * server-side dedup also requires an identical `distinct_id`, which `reset()` rotates after
 * every order — so late re-fires get through and are billed. This registry suppresses them
 * app-side, keyed by the `$insert_id` already sent.
 *
 * The registry is a claim protocol (MR248 review): `tryReserveEvent` checks and claims an
 * id in one call, verifying the claim by reading its ownership nonce back — localStorage
 * has no compare-and-swap, so when two instances race the same id, whoever's write survives
 * owns it and the other caller yields. The SDK callback then either confirms the entry
 * (long TTL) or releases it, both gated on the owning token so a late callback can never
 * settle a reservation that was retried by another instance after expiry. A reservation
 * whose hand-off never reports back expires on its own short TTL and becomes retryable.
 *
 * Every insert id lives under its own localStorage key. The atomic unit of localStorage is
 * a single `setItem`/`removeItem`, so a shared map would make each claim a whole-map
 * read-modify-write in which two instances claiming DIFFERENT ids from the same snapshot
 * overwrite each other — the displaced claim vanished and its id became re-fireable. With
 * one key per id, an instance only ever touches the slot of the id it is claiming, and the
 * TTL/cap sweep only ever removes keys, so concurrent sweeps cannot corrupt each other.
 *
 * Ghost instances share this origin's localStorage, so storage is re-read on every call —
 * an in-memory cache would hide claims written by another instance. Storage failures fail
 * open (the event is tracked): a duplicate is cheaper than a lost order event.
 */
export const SENT_EVENTS_KEY_PREFIX = "analyticsSentOrderEvents:";

export const sentEventStorageKey = (insertId: string): string => `${SENT_EVENTS_KEY_PREFIX}${insertId}`;

// Re-fires arrive within hours (a stale ghost realm answers newborns after ~11-16h), never
// days later; the TTL bounds storage growth on a 24/7 kiosk.
const SENT_EVENT_TTL_MS = 48 * 60 * 60 * 1000;
// A reservation whose SDK callback never fired (page killed mid-hand-off) suppresses
// re-fires at most this long before the event becomes retryable again.
const PENDING_RESERVATION_TTL_MS = 60 * 1000;
// Safety net if TTL pruning is defeated (e.g. a stuck clock): ~500 keys ≈ 50 KB.
const SENT_EVENTS_MAX_ENTRIES = 500;

type SentEventEntry = { at: number; confirmed: boolean; nonce?: string };

const isEntry = (value: unknown): value is SentEventEntry => {
	if (typeof value !== "object" || value === null) {
		return false;
	}
	const entry = value as SentEventEntry;
	return (
		typeof entry.at === "number" &&
		typeof entry.confirmed === "boolean" &&
		(entry.nonce === undefined || typeof entry.nonce === "string")
	);
};

// Validate at the boundary instead of asserting: a corrupt value must not smuggle a
// malformed entry into the typed protocol — it reads as "no entry".
const parseEntry = (raw: string | null): SentEventEntry | undefined => {
	if (raw === null) {
		return undefined;
	}
	try {
		const parsed: unknown = JSON.parse(raw);
		return isEntry(parsed) ? parsed : undefined;
	} catch {
		return undefined;
	}
};

const readEntry = (insertId: string): SentEventEntry | undefined =>
	parseEntry(safeGetItem(sentEventStorageKey(insertId)));

// A dead registry silently disables dedup fleet-wide — surface that once per app instance.
let hasReportedWriteFailure = false;

/** @returns whether the write reached storage. */
const writeEntry = (insertId: string, entry: SentEventEntry): boolean => {
	const persisted = safeSetItem(sentEventStorageKey(insertId), JSON.stringify(entry));
	if (!(persisted || hasReportedWriteFailure)) {
		hasReportedWriteFailure = true;
		logToSentry("Failed to persist the sent-events registry", "warning");
	}
	return persisted;
};

const entryTtlMs = (entry: SentEventEntry): number =>
	entry.confirmed ? SENT_EVENT_TTL_MS : PENDING_RESERVATION_TTL_MS;

const isEntryLive = (entry: SentEventEntry): boolean =>
	// A negative age (timestamp in the future after a backward clock jump) still counts.
	Date.now() - entry.at < entryTtlMs(entry);

/**
 * Remove a key only if it still holds the value the sweep decided on. Another instance can
 * re-claim an expired id between the sweep's read and its removal; re-reading right before
 * the removal shrinks that window from the whole scan to one getItem→removeItem pair.
 * (localStorage has no compare-and-delete, so it cannot be closed outright — a claim lost
 * here fails open on its owner's read-back and costs at most one duplicate.)
 */
const removeIfUnchanged = (key: string, inspected: string | null): void => {
	if (safeGetItem(key) === inspected) {
		safeRemoveItem(key);
	}
};

/**
 * TTL-prune and make room for one insertion. Timestamps ahead of the clock (backward clock
 * jump, forward excursion) are clamped to now so they age out normally instead of surviving
 * eviction as forever-newest; the oldest entries are evicted down to MAX - 1 so the key the
 * caller is about to write always survives the sweep. Corrupt values are dropped.
 */
const pruneForInsert = (): void => {
	const now = Date.now();
	const live: Array<{ key: string; raw: string | null; at: number }> = [];
	for (const key of safeKeys(SENT_EVENTS_KEY_PREFIX)) {
		const raw = safeGetItem(key);
		const entry = parseEntry(raw);
		if (entry === undefined) {
			removeIfUnchanged(key, raw);
			continue;
		}
		const at = Math.min(entry.at, now);
		if (now - at >= entryTtlMs(entry)) {
			removeIfUnchanged(key, raw);
			continue;
		}
		let stored = raw;
		if (at !== entry.at) {
			const clamped = JSON.stringify({ ...entry, at: at });
			if (safeSetItem(key, clamped)) {
				stored = clamped;
			}
		}
		live.push({ key: key, raw: stored, at: at });
	}
	if (live.length < SENT_EVENTS_MAX_ENTRIES) {
		return;
	}
	live.sort((a, b) => b.at - a.at);
	for (const { key, raw } of live.slice(SENT_EVENTS_MAX_ENTRIES - 1)) {
		removeIfUnchanged(key, raw);
	}
};

export const hasEventBeenSent = (insertId: string): boolean => {
	const entry = readEntry(insertId);
	return entry !== undefined && isEntryLive(entry);
};

/**
 * Check and claim an insert id in one call. Returns the ownership token on success, or
 * null when the id is already sent/claimed (the caller must not track the event). When
 * storage is unavailable no cross-instance coordination exists — the claim then succeeds
 * unconditionally, preferring a duplicate over a lost event.
 */
export const tryReserveEvent = (insertId: string): string | null => {
	pruneForInsert();
	const existing = readEntry(insertId);
	if (existing && isEntryLive(existing)) {
		return null;
	}

	const nonce = uuid();
	if (!writeEntry(insertId, { at: Date.now(), confirmed: false, nonce: nonce })) {
		return nonce;
	}

	// localStorage has no compare-and-swap: another instance's claim of the SAME id can land
	// between our read and our write. Read the slot back — whoever's write survived owns the
	// id; if it is not us, yield. (An absent entry after a successful write means storage
	// misbehaved: fail open.)
	const stored = readEntry(insertId);
	if (stored === undefined || stored.nonce === nonce) {
		return nonce;
	}
	return null;
};

/** The SDK accepted the event — keep the id suppressed for the full TTL. Owner-gated. */
export const confirmEventSent = (insertId: string, token: string): void => {
	const entry = readEntry(insertId);
	if (!entry || entry.confirmed || entry.nonce !== token) {
		return;
	}
	writeEntry(insertId, { at: Date.now(), confirmed: true });
};

/** The hand-off failed — free the id for an immediate retry. Owner-gated, pending only. */
export const releaseEvent = (insertId: string, token: string): void => {
	const entry = readEntry(insertId);
	if (!entry || entry.confirmed || entry.nonce !== token) {
		return;
	}
	safeRemoveItem(sentEventStorageKey(insertId));
};
